// Enkel överblick: procent på processens grundsteg + räknare för löpande arbete.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { PROCESS_PHASES, PROCESS_STEPS } from "@/lib/basProcess/constants";
import { Gauge } from "lucide-react";

interface Props {
  projectId: string;
}

interface Counters {
  workPreparations: number;
  workPreparationsPending: number;
  safetyRounds: number;
  lastRound: string | null;
  incidents: number;
  incidentsOpen: number;
  submissionsPending: number;
  qaOpen: number;
}

const pct = (done: number, total: number) => (total === 0 ? 0 : Math.round((done / total) * 100));

const ProjectProgressPanel: React.FC<Props> = ({ projectId }) => {
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [external, setExternal] = useState<Record<string, boolean>>({});
  const [counters, setCounters] = useState<Counters | null>(null);

  const load = useCallback(async () => {
    const [steps, ext, wp, wpPending, sr, lastSr, inc, incOpen, subs, qa] = await Promise.all([
      supabase.from("process_steps").select("doc_number, status").eq("project_id", projectId),
      supabase
        .from("external_step_completions")
        .select("doc_number, completed")
        .eq("project_id", projectId),
      supabase
        .from("work_preparations")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId),
      supabase
        .from("work_preparations")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .in("status", ["under_granskning", "review", "pending"]),
      supabase
        .from("safety_rounds")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId),
      supabase
        .from("safety_rounds")
        .select("round_date")
        .eq("project_id", projectId)
        .order("round_date", { ascending: false })
        .limit(1),
      supabase
        .from("incident_reports")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId),
      supabase
        .from("incident_reports")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .neq("status", "closed"),
      supabase
        .from("designer_submissions")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .eq("status", "submitted"),
      supabase
        .from("qa_log_entries")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .neq("status", "besvarad"),
    ]);

    const map: Record<string, string> = {};
    (steps.data || []).forEach((s: { doc_number: string; status: string }) => {
      map[s.doc_number] = s.status;
    });
    setStatuses(map);

    const extMap: Record<string, boolean> = {};
    (ext.data || []).forEach((e: { doc_number: string; completed: boolean }) => {
      extMap[e.doc_number] = e.completed;
    });
    setExternal(extMap);

    setCounters({
      workPreparations: wp.count ?? 0,
      workPreparationsPending: wpPending.count ?? 0,
      safetyRounds: sr.count ?? 0,
      lastRound: (lastSr.data?.[0] as { round_date?: string } | undefined)?.round_date ?? null,
      incidents: inc.count ?? 0,
      incidentsOpen: incOpen.count ?? 0,
      submissionsPending: subs.count ?? 0,
      qaOpen: qa.count ?? 0,
    });
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const isDone = (doc: string) => {
    const s = statuses[doc];
    return s === "done" || s === "not_applicable" || external[doc] === true;
  };

  const inactiveCount = PROCESS_STEPS.filter(
    (s) => statuses[s.docNumber] === "not_applicable"
  ).length;

  const totalDone = PROCESS_STEPS.filter((s) => isDone(s.docNumber)).length;
  const totalPct = pct(totalDone, PROCESS_STEPS.length);

  const daysSince = (d: string | null) => {
    if (!d) return null;
    return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge className="h-4 w-4" /> Överblick
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Processens grundsteg</span>
            <span className="text-2xl font-semibold">{totalPct}%</span>
          </div>
          <Progress value={totalPct} />
          <p className="mt-1 text-xs text-muted-foreground">
            {totalDone} av {PROCESS_STEPS.length} dokumentsteg är klara eller markerade som ej
            tillämpliga. Procenten räknar bara de dokument som ska tas fram en gång i projektet –
            löpande arbete som arbetsberedningar, skyddsronder och tillbud räknas separat nedan.
          </p>
          {inactiveCount > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {inactiveCount} dokument är markerade som ej aktuella i lättläge och göms i flikarna.
              Du kan visa dem igen med knappen "Visa även ej aktuella" ovanför flikarna.
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_PHASES.map((p) => {
            const steps = PROCESS_STEPS.filter((s) => s.phase === p.key);
            const done = steps.filter((s) => isDone(s.docNumber)).length;
            const value = pct(done, steps.length);
            return (
              <div key={p.key} className="rounded-md border p-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium">{p.label}</span>
                  <span className="text-sm">{value}%</span>
                </div>
                <Progress value={value} className="mt-2 h-1.5" />
                <p className="mt-1 text-xs text-muted-foreground">
                  {done} av {steps.length} steg
                </p>
              </div>
            );
          })}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Löpande arbete</p>
          <p className="mb-2 text-xs text-muted-foreground">
            Det här räknas inte i procent – det pågår så länge projektet pågår.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              {counters?.workPreparations ?? 0} arbetsberedningar
              {counters?.workPreparationsPending
                ? ` · ${counters.workPreparationsPending} väntar på granskning`
                : ""}
            </Badge>
            <Badge variant="secondary">
              {counters?.safetyRounds ?? 0} skyddsronder
              {daysSince(counters?.lastRound ?? null) !== null
                ? ` · senaste för ${daysSince(counters?.lastRound ?? null)} dagar sedan`
                : ""}
            </Badge>
            <Badge variant="secondary">
              {counters?.incidents ?? 0} tillbud/olyckor
              {counters?.incidentsOpen ? ` · ${counters.incidentsOpen} öppna` : ""}
            </Badge>
            <Badge variant="secondary">
              {counters?.submissionsPending ?? 0} projektörsinlämningar att kvittera
            </Badge>
            <Badge variant="secondary">{counters?.qaOpen ?? 0} obesvarade frågor</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectProgressPanel;
